<template>
  <transition name="fade">
    <div class="modal" v-if="showGameRules">
      <div class="modal-header">
        <h2>How to Play Juicy Truth</h2>
        <span class="close-modal" @click="close">✕</span>
      </div>
      <div class="modal-body">
        <div class="rules-content">
          <p class="intro">This isn't just a game—it's a portal to deeper connection. The goal? To peel back the layers and squeeze out the sweet nectar. Whether you're with a group, a partner, or flying solo, the win is in discovering something about the other person, but most importantly, about discovering something about yourself.</p>

          <h3>✨ Core Flow: One-on-One or Partnered Play</h3>

          <h4>Choose Your Flavor:</h4>
          <p><strong>"Truth or Juicy Truth?"</strong><br>
          <em>Truth</em> = thoughtful, reflective, fun, light hearted questions, some with a bit of an edge<br>
          <em>Juicy Truth</em> = deeper, personal questions that require vulnerability and deep reflection</p>

          <h4>Ask the Question:</h4>
          <ul>
            <li>Read the main question out loud. Let it land.</li>
            <li>The responder pauses, and reflects.</li>
            <li>It is encouraged not to just say the first thing that comes to mind. Be inquisitive: What's beneath that?</li>
          </ul>

          <h4>Go Deeper:</h4>
          <p>Once the first response is shared, the questioner asks the Go Deeper prompt. Or —they can follow their own curiosity:</p>
          <ul>
            <li>Ask for more detail.</li>
            <li>Gently challenge an assumption.</li>
            <li>Reflect something back to encourage introspection.</li>
          </ul>
          <p class="emphasis">The magic is in the follow-up.</p>

          <h4>Switch Roles:</h4>
          <p>Now it's the responder's turn to ask. You can ask a new question, or both take turns asking the same one.</p>
          <p class="note">Don't be surprised if you get lost down a rabbit hole on one question!</p>

          <h3>Group Play Options</h3>

          <h4>One Question, Many Truths:</h4>
          <p>Pick a player to answer first, then invite everyone to share their own take on the same question.</p>

          <h4>Round Table Reflections:</h4>
          <p>Each person gets their own unique question. Let the circle of stories unfold.</p>

          <h3>Solo Exploration</h3>
          <p>Use Juicy Truth as a journaling ritual or self-reflection practice:</p>

          <ol>
            <li><strong>Read the Question.</strong><br>Sit with it. What is your immediate answer?</li>
            <li><strong>Write it Down.</strong><br>Capture your first response without judgment.</li>
            <li><strong>Pause & Meditate.</strong><br>Close your eyes. Breathe. Sit with the question for 2–5 minutes.</li>
            <li><strong>Go Deeper.</strong><br>Now write what's beneath that first answer. What truth are you uncovering now?</li>
          </ol>

          <div class="remember-box">
            <h4>💡 Remember:</h4>
            <ul>
              <li>This isn't about "right answers." It's about presence, curiosity, and courageous honesty.</li>
              <li>Dare to get juicy. Dare to be real.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { useStore } from '../store';
import { mapState } from 'pinia';

export default {
  computed: {
    ...mapState(useStore, ['showGameRules']),
  },
  methods: {
    close() {
      useStore().showGameRules = false;
    },
  },
};
</script>

<style scoped>
.modal {
    position: fixed;
    top: 50%;
    left: 50%;
    width: 80%;
    max-width: 600px;
    background: white;
    transform: translate(-50%, -50%);
    padding: 20px;
    z-index: 15000;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    border-radius: 15px;
    transition: background-color 0.3s ease, color 0.3s ease;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.modal-header h2 {
    margin: 0;
    color: #333;
    transition: color 0.3s ease;
}

.close-modal {
    cursor: pointer;
    font-size: 24px;
    color: #666;
    transition: color 0.3s ease;
}

.close-modal:hover {
    color: #333;
}

.modal-body {
    margin-top: 10px;
    color: #333;
    transition: color 0.3s ease;
}

body.dark-mode .modal {
    background-color: var(--background-dark);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
}

body.dark-mode .modal-header h2 {
    color: var(--text-dark);
}

body.dark-mode .close-modal {
    color: #999;
}

body.dark-mode .close-modal:hover {
    color: var(--text-dark);
}

body.dark-mode .modal-body {
    color: var(--text-dark);
}

.rules-content {
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 10px;
}

.rules-content h3 {
  color: var(--raspberry-pink);
  margin: 1.5em 0 0.5em;
  font-size: 1.3em;
}

.rules-content h4 {
  color: var(--coral);
  margin: 1em 0 0.5em;
  font-size: 1.1em;
}

.rules-content p {
  margin: 0.8em 0;
  line-height: 1.5;
}

.rules-content .intro {
  font-size: 1.1em;
  line-height: 1.6;
  margin-bottom: 1.5em;
}

.rules-content ul,
.rules-content ol {
  margin: 0.8em 0;
  padding-left: 1.5em;
}

.rules-content li {
  margin: 0.5em 0;
  line-height: 1.5;
}

.rules-content .emphasis {
  font-style: italic;
  color: var(--raspberry-pink);
  text-shadow:
      0.5px 0 0 currentColor,
      1px 0 0 currentColor,
      1.5px 0 0 currentColor;
}

.rules-content .note {
  font-style: italic;
  color: var(--coral);
}

.rules-content .remember-box {
  background-color: rgba(255, 61, 91, 0.1);
  border-radius: 10px;
  padding: 1em;
  margin: 1.5em 0;
}

.rules-content .remember-box h4 {
  margin-top: 0;
  color: var(--raspberry-pink);
}

.rules-content .remember-box ul {
  margin: 0.5em 0;
}

body.dark-mode .rules-content h3 {
  color: var(--peach);
}

body.dark-mode .rules-content h4 {
  color: var(--coral);
}

body.dark-mode .rules-content .emphasis {
  color: var(--peach);
}

body.dark-mode .rules-content .note {
  color: var(--coral);
}

body.dark-mode .rules-content .remember-box {
  background-color: rgba(255, 61, 91, 0.2);
}

.rules-content::-webkit-scrollbar {
  width: 8px;
}

.rules-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.rules-content::-webkit-scrollbar-thumb {
  background: var(--raspberry-pink);
  border-radius: 4px;
}

body.dark-mode .rules-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

body.dark-mode .rules-content::-webkit-scrollbar-thumb {
  background: var(--peach);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
